import { useEffect, useMemo, useRef } from "react";

import type { ChecklistItem, ChecklistRowApplicability, PlanReviewChecklist } from "@/api/plan_review";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  describeNumericRule,
  sectionHeadingsFor,
} from "@/components/plan_review/ChecklistProposalEditor";

const APPLICABILITY_LABELS: Record<ChecklistRowApplicability, string> = {
  required: "Required",
  optional: "Optional",
  not_applicable: "N/A",
};

/** Rows predate the applicability field, so fall back to the boolean. */
function applicabilityOf(item: ChecklistItem): ChecklistRowApplicability {
  return item.applicability ?? (item.required ? "required" : "optional");
}

/** What the reviewer has to find for this row, when it is more than presence. */
function verificationLabel(item: ChecklistItem): string | null {
  if (item.verification_mode === "numeric_rule") {
    const rule = describeNumericRule(item.numeric_rule);
    return rule ? `Checks ${rule}` : "Checks a number";
  }
  if (item.verification_mode === "presence_with_value") return "Needs a value";
  return null;
}

/**
 * Read-only view of a saved checklist's items, so a planner can see what an
 * extraction produced without re-uploading the document. The editable
 * sibling is ChecklistProposalEditor, which runs before anything is saved.
 */
export function ChecklistDetailDialog({
  checklist,
  onOpenChange,
}: {
  checklist: PlanReviewChecklist | null;
  onOpenChange: (open: boolean) => void;
}) {
  // Radix keeps the dialog mounted through its close animation, so hold
  // the last checklist: reading a nulled prop would blank the title and
  // flash "no items" on the way out.
  const lastShown = useRef<PlanReviewChecklist | null>(checklist);
  useEffect(() => {
    if (checklist) lastShown.current = checklist;
  }, [checklist]);
  const shown = checklist ?? lastShown.current;
  const items = shown?.items ?? [];

  const sectionHeadings = useMemo(() => sectionHeadingsFor(items), [items]);

  return (
    <Dialog open={checklist !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="pr-6">{shown?.name}</DialogTitle>
          <DialogDescription>
            {items.length} item{items.length === 1 ? "" : "s"} on this checklist
            {shown?.original_filename ? `, from ${shown.original_filename}` : ""}. Reviews run
            against these rows.
          </DialogDescription>
        </DialogHeader>
        {/* min-w-0: DialogContent is a grid, and a long source line would
            otherwise set the track's min-content width and overflow it. */}
        <ol
          className="ordinal-scrollbar max-h-[55vh] w-full min-w-0 overflow-y-auto rounded-md border border-border text-sm"
          data-testid="checklist-detail-items"
          aria-label={`Items on ${shown?.name ?? "checklist"}`}
        >
          {items.map((item, index) => {
            const sectionHeading = sectionHeadings[index];
            const position = index + 1;
            const sourceText = item.source_text?.trim();
            const showSource = !!sourceText && sourceText !== item.label.trim();
            const verification = verificationLabel(item);
            const applicability = applicabilityOf(item);
            return (
              <li
                key={item.key ?? `${position}-${item.label}`}
                className="border-b border-border last:border-b-0"
                data-testid="checklist-detail-item"
              >
                {sectionHeading ? (
                  <div className="bg-muted/50 px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {sectionHeading}
                  </div>
                ) : null}
                <div className="flex items-start gap-2 px-3 py-2">
                  <span className="w-7 shrink-0 tabular-nums text-muted-foreground">{position}.</span>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="min-w-0 break-words text-foreground">{item.label}</span>
                      <Badge variant={applicability === "required" ? "secondary" : "outline"}>
                        {APPLICABILITY_LABELS[applicability]}
                      </Badge>
                      {verification ? <Badge variant="outline">{verification}</Badge> : null}
                    </div>
                    {showSource ? (
                      <p className="min-w-0 break-words text-xs text-muted-foreground">
                        Found: “{sourceText}”
                      </p>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
          {items.length === 0 ? (
            <li className="px-3 py-4 text-center text-xs text-muted-foreground">
              This checklist has no items.
            </li>
          ) : null}
        </ol>
        <DialogFooter>
          {/* "Done", not "Close": DialogContent already ships an X whose
              accessible name is Close. */}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
